import { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from './ui/button';

export const SortButton = ({ onSort, currentSort, field }) => {
  const handleClick = () => {
    if (currentSort.field === field) {
      // Toggle direction
      onSort(field, currentSort.direction === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to desc (newest first)
      onSort(field, 'desc');
    }
  };

  const isActive = currentSort.field === field;
  
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={`h-8 px-2 ${isActive ? 'text-blue-600' : ''}`}
    >
      {isActive ? (
        currentSort.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
      ) : (
        <ArrowUpDown className="w-4 h-4 opacity-50" />
      )}
    </Button>
  );
};

export const DateRangeFilter = ({ fromDate, toDate, onFromDateChange, onToDateChange }) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label className="text-xs">From Date</Label>
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => onFromDateChange(e.target.value)}
          className="h-9"
        />
      </div>
      <div>
        <Label className="text-xs">To Date</Label>
        <Input
          type="date"
          value={toDate}
          onChange={(e) => onToDateChange(e.target.value)}
          className="h-9"
        />
      </div>
    </div>
  );
};

export const SearchFilters = ({
  lessees = [],
  riders = [],
  motorcycles = [],
  technicians = [],
  selectedLessee,
  selectedRider,
  selectedBike,
  selectedTechnician,
  onLesseeChange,
  onRiderChange,
  onBikeChange,
  onTechnicianChange,
  showLessee = true,
  showRider = true,
  showBike = true,
  showTechnician = false
}) => {
  const hasAnyFilter = showLessee || showRider || showBike || showTechnician;
  const activeFilters = [showLessee, showRider, showBike, showTechnician].filter(Boolean).length;
  
  return (
    <div className={`grid grid-cols-1 gap-4 ${
      activeFilters === 4 ? 'md:grid-cols-4' :
      activeFilters === 3 ? 'md:grid-cols-3' :
      activeFilters === 2 ? 'md:grid-cols-2' :
      'md:grid-cols-1'
    }`}>
      {showLessee && (
        <div>
          <Label className="text-xs">Filter by Lessee</Label>
          <SearchableSelect
            options={lessees.map(l => ({
              value: l.company_name || l.name,
              label: l.company_name || l.name
            }))}
            value={selectedLessee}
            onValueChange={onLesseeChange}
            placeholder="All lessees..."
            emptyMessage="No lessees found"
            className="h-9"
          />
        </div>
      )}
      {showRider && (
        <div>
          <Label className="text-xs">Filter by Rider</Label>
          <SearchableSelect
            options={riders.map(r => ({
              value: r.name,
              label: r.name
            }))}
            value={selectedRider}
            onValueChange={onRiderChange}
            placeholder="All riders..."
            emptyMessage="No riders found"
            className="h-9"
          />
        </div>
      )}
      {showBike && (
        <div>
          <Label className="text-xs">Filter by Bike</Label>
          <SearchableSelect
            options={motorcycles.map(m => ({
              value: m.plate_number,
              label: m.plate_number
            }))}
            value={selectedBike}
            onValueChange={onBikeChange}
            placeholder="All bikes..."
            emptyMessage="No bikes found"
            className="h-9"
          />
        </div>
      )}
      {showTechnician && (
        <div>
          <Label className="text-xs">Filter by Technician</Label>
          <SearchableSelect
            options={(technicians || []).map(t => ({
              value: t.name,
              label: t.name,
              metadata: t.specialization ? { 'Specialization': t.specialization } : {}
            }))}
            value={selectedTechnician}
            onValueChange={onTechnicianChange}
            placeholder={technicians && technicians.length > 0 ? "All technicians..." : "Loading technicians..."}
            emptyMessage="No technicians found"
            className="h-9"
          />
        </div>
      )}
    </div>
  );
};

// Helper to parse various date formats into Date object
const parseDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  
  // If already a Date object
  if (dateStr instanceof Date) return dateStr;
  
  // Handle DD-MM-YYYY format (e.g., "12-02-2026" or "9-2-2026")
  const ddmmyyyy = String(dateStr).match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (ddmmyyyy) {
    const day = parseInt(ddmmyyyy[1], 10);
    const month = parseInt(ddmmyyyy[2], 10) - 1; // JavaScript months are 0-indexed
    const year = parseInt(ddmmyyyy[3], 10);
    return new Date(year, month, day);
  }
  
  // Try standard ISO format first
  let date = new Date(dateStr);
  if (!isNaN(date.getTime())) return date;
  
  // Try parsing "DD Mon YYYY" format (e.g., "04 Jan 2026")
  const monthMap = {
    'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
    'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
  };
  
  const parts = String(dateStr).match(/(\d{1,2})\s+(\w{3})\s+(\d{4})/i);
  if (parts) {
    const day = parseInt(parts[1], 10);
    const month = monthMap[parts[2].toLowerCase()];
    const year = parseInt(parts[3], 10);
    if (month !== undefined) {
      return new Date(year, month, day);
    }
  }
  
  // Return epoch if nothing works
  return new Date(0);
};

export const sortData = (data, field, direction = 'desc') => {
  return [...data].sort((a, b) => {
    let aVal = a[field];
    let bVal = b[field];
    
    // Handle date fields
    if (field.includes('date') || field.includes('Date')) {
      const aDate = parseDate(aVal);
      const bDate = parseDate(bVal);
      return direction === 'asc' ? aDate - bDate : bDate - aDate;
    }
    
    // Handle numeric fields
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    // Handle string fields
    const aStr = String(aVal || '').toLowerCase();
    const bStr = String(bVal || '').toLowerCase();
    
    if (direction === 'asc') {
      return aStr.localeCompare(bStr);
    } else {
      return bStr.localeCompare(aStr);
    }
  });
};

export const filterData = (data, filters) => {
  return data.filter(item => {
    // Date range filter
    if (filters.fromDate && filters.toDate) {
      const itemDate = new Date(item.date || item.salik_date || item.date_of_fine || item.created_at);
      const from = new Date(filters.fromDate);
      const to = new Date(filters.toDate);
      if (itemDate < from || itemDate > to) return false;
    }
    
    // Lessee filter
    if (filters.lessee && filters.lessee !== '') {
      const lesseeMatch = (item.lessee_name || '').toLowerCase().includes(filters.lessee.toLowerCase());
      if (!lesseeMatch) return false;
    }
    
    // Rider filter
    if (filters.rider && filters.rider !== '') {
      const riderMatch = (item.rider_name || '').toLowerCase().includes(filters.rider.toLowerCase());
      if (!riderMatch) return false;
    }
    
    // Bike filter
    if (filters.bike && filters.bike !== '') {
      const bikeMatch = (item.motorcycle_plate || item.bike_plate_number || '').toLowerCase().includes(filters.bike.toLowerCase());
      if (!bikeMatch) return false;
    }
    
    // Technician filter
    if (filters.technician && filters.technician !== '') {
      const technicianMatch = (item.technician || item.technician_name || '').toLowerCase().includes(filters.technician.toLowerCase());
      if (!technicianMatch) return false;
    }
    
    // Location filter
    if (filters.location && filters.location !== '') {
      const locationMatch = (item.location || item.location_name || '').toLowerCase().includes(filters.location.toLowerCase());
      if (!locationMatch) return false;
    }
    
    return true;
  });
};
